import { message } from 'antd'
import { useInfiniteQuery, useQuery } from 'react-query'
import { isEmpty } from 'lodash-es'
import {
  getCreditMemoLog,
  getDebitMemoLog,
  getFinanceBankTransLog,
  getFinanceContactLog,
  getFinanceDeliveryLog,
  getFinanceExpenseLog,
  getFinanceFixedAssetLog,
  getFinanceFixedAssetsAutomaticDepreciateLogs,
  getFinanceInvoiceLog,
  getFinanceLogs,
  getFinanceManualJournalLog,
  getFinanceNumberDetailLog,
  getFinanceNumberLog,
  getFinanceOrderLog,
  getFinanceProductLog,
  getFinancePurchaseDeliveryLog,
  getFinancePurchaseInvoiceLog,
  getFinancePurchaseOrderLog,
  getFinancePurchaseQuoteLog,
  getFinanceQuoteLog,
  getFinanceStockAdjustmentLog,
  getFinanceTagLog,
  getFinanceWareHouseLog,
  getFinanceWareHouseTransferLog,
  getInvoicePaymentCreditMemoLogs,
  getJoinedInvoiceLog,
  getJoinedPurchaseInvoiceLog,
  getMaterialRequestLog,
  getMaterialIssueLog,
  getProformaInvoiceLog,
  getProformaOrderLog,
  getPurchaseInvoicePaymentDebitMemoLogs,
  getPurchaseReturnLog,
  getReturnLog,
} from 'utils/apis'
import { getFinanceLogItems, getLogByTransType } from 'utils/apis/audit'
import { transType } from 'utils/static'
// import { transType } from 'utils/static'

export const useFinanceLogs = ({ options = {} } = {}) => {
  return useQuery(
    ['financeLogs'],
    async () => {
      const {
        data: { data },
      } = await getFinanceLogs()
      return data
    },
    {
      onError: (error) => message.error(error?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useFinanceLogItems = ({ options = {} } = {}) => {
  return useQuery(
    ['financeLogItems'],
    async () => {
      const {
        data: { data },
      } = await getFinanceLogItems()
      return data
    },
    {
      onError: (error) => message.error(error?.data?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useInfiniteFinanceLogs = ({ params = {}, options = {} } = {}) => {
  return useInfiniteQuery(
    ['infiniteFinanceLogs', params],
    async ({ pageParam = 1 }) => {
      const {
        data: { data },
      } = await getFinanceLogs({ params: { page: pageParam, ...params } })
      return data
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined
        }
        return lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined
      },
      onError: (error) => message.error(error?.data?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

const bankTransTypes = [
  transType.SPEND_MONEY,
  transType.RECEIVE_MONEY,
  transType.EXPENSE_PAYMENT,
  transType.PURCHASE_PAYMENT,
  transType.PURCHASE_ORDER_PAYMENT,
  transType.RECEIVE_INVOICE_PAYMENT,
  transType.RECEIVE_SALES_ORDER_PAYMENT,
  transType.SEND_TRANSFER,
  transType.RECEIVE_TRANSFER,
  transType.CREDIT_MEMO_PAYMENT,
  transType.DEBIT_MEMO_PAYMENT,
  transType.PURCHASE_JOIN_INVOICE_PAYMENT,
  transType.RECEIVE_JOIN_INVOICE_PAYMENT,
  transType.INVOICE_WITHOLDING,
  transType.PURCHASE_INVOICE_WITHOLDING,
  transType.EXPENSE_WITHOLDING,
]

export const useInfiniteLogs = (payload, options = {}) => {
  // Jika ada params trans_type_id berupa Array maka endpoint berubah
  const isByTransTypes = Array.isArray(payload.trans_type_id)
  let request = getFinanceLogs
  if (!isEmpty(payload.trans_type_id) && isByTransTypes) {
    request = getLogByTransType
  } else if (payload.tran_id && payload.trans_type_id === transType.DELIVERY) {
    request = getFinanceDeliveryLog
  } else if (payload.tran_id && payload.trans_type_id === transType.INVOICE) {
    request = getFinanceInvoiceLog
  } else if (payload.tran_id && payload.trans_type_id === transType.ORDER) {
    request = getFinanceOrderLog
  } else if (payload.tran_id && payload.trans_type_id === transType.PROFORMA_INVOICE) {
    request = getProformaInvoiceLog
  } else if (payload.tran_id && payload.trans_type_id === transType.PROFORMA_ORDER) {
    request = getProformaOrderLog
  } else if (payload.tran_id && payload.trans_type_id === transType.QUOTE) {
    request = getFinanceQuoteLog
  } else if (payload.tran_id && payload.trans_type_id === transType.PURCHASE_DELIVERY) {
    request = getFinancePurchaseDeliveryLog
  } else if (payload.tran_id && payload.trans_type_id === transType.PURCHASE_INVOICE) {
    request = getFinancePurchaseInvoiceLog
  } else if (payload.tran_id && payload.trans_type_id === transType.PURCHASE_ORDER) {
    request = getFinancePurchaseOrderLog
  } else if (payload.tran_id && payload.trans_type_id === transType.PURCHASE_QUOTE) {
    request = getFinancePurchaseQuoteLog
  } else if (payload.tran_id && payload.trans_type_id === transType.EXPENSE) {
    request = getFinanceExpenseLog
  } else if (payload.tran_id && payload.trans_type_id === transType.FIXED_ASSET) {
    request = getFinanceFixedAssetLog
  } else if (payload.tran_id && payload.trans_type_id === transType.CONTACT) {
    request = getFinanceContactLog
  } else if (payload.tran_id && payload.trans_type_id === transType.PRODUCT) {
    request = getFinanceProductLog
  } else if (payload.tran_id && payload.trans_type_id === transType.NUMBER) {
    request = getFinanceNumberDetailLog
  } else if (payload.tran_id && payload.trans_type_id === transType.STOCK_ADJUSTMENT) {
    request = getFinanceStockAdjustmentLog
  } else if (payload.tran_id && payload.trans_type_id === transType.WAREHOUSE_TRANSFER) {
    request = getFinanceWareHouseTransferLog
  } else if (payload.tran_id && payload.trans_type_id === transType.TAGS) {
    request = getFinanceTagLog
  } else if (payload.tran_id && payload.trans_type_id === transType.INVOICE_PAYMENT_CREDIT_MEMO) {
    request = getInvoicePaymentCreditMemoLogs
  } else if (
    payload.tran_id &&
    payload.trans_type_id === transType.PURCHASE_INVOICE_PAYMENT_DEBIT_MEMO
  ) {
    request = getPurchaseInvoicePaymentDebitMemoLogs
  } else if (payload.tran_id && payload.trans_type_id === transType.RETURN) {
    request = getReturnLog
  } else if (payload.tran_id && payload.trans_type_id === transType.PURCHASE_RETURN) {
    request = getPurchaseReturnLog
  } else if (payload.tran_id && payload.trans_type_id === transType.CREDIT_MEMO) {
    request = getCreditMemoLog
  } else if (payload.tran_id && payload.trans_type_id === transType.DEBIT_MEMO) {
    request = getDebitMemoLog
  } else if (payload.tran_id && bankTransTypes.includes(payload.trans_type_id)) {
    request = getFinanceBankTransLog
  } else if (payload.source === 'automaticDepreciationLogs') {
    request = getFinanceFixedAssetsAutomaticDepreciateLogs
  } else if (payload.tran_id && payload.trans_type_id === transType.MANUAL_JOURNAL) {
    request = getFinanceManualJournalLog
  } else if (payload.source === 'numberLogs') {
    request = getFinanceNumberLog
  } else if (payload.tran_id && payload.trans_type_id === transType.WAREHOUSE) {
    request = getFinanceWareHouseLog
  } else if (payload.tran_id && payload.trans_type_id === transType.JOIN_INVOICE) {
    request = getJoinedInvoiceLog
  } else if (payload.tran_id && payload.trans_type_id === transType.JOIN_PURCHASE_INVOICE) {
    request = getJoinedPurchaseInvoiceLog
  } else if (payload.tran_id && payload.trans_type_id === transType.MATERIAL_REQUEST) {
    request = getMaterialRequestLog
  } else if (payload.tran_id && payload.trans_type_id === transType.MATERIAL_ISSUE) {
    request = getMaterialIssueLog
  }

  return useInfiniteQuery(
    ['logs', payload],
    async ({ pageParam = 0 }) => {
      let response
      try {
        const params = { page: pageParam, ...payload }
        if (params.tran_id && !isByTransTypes) {
          delete params.trans_type_id
        }
        if (isByTransTypes) {
          params.trans_type_id = params.trans_type_id.join(',')
        }
        let _response
        if (params.tran_id) {
          _response = await request({ id: params.tran_id, params })
        } else {
          _response = await request({ params })
        }

        response = _response.data.data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined
        }
        return lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined
      },
      onError: (error) => message.error(error.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}
